/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';

/** The app bar's colour. A machine page tints it with the machine's status. */
export type AppBarTone =
  | 'focus'
  | 'positive'
  | 'warning'
  | 'negative'
  | 'neutral';

interface HeaderContextValue {
  mobileActionButton: ReactNode | null;
  setMobileActionButton: (button: ReactNode | null) => void;
  pageTitle: string | null;
  setPageTitle: (title: string | null) => void;
  appBarTone: AppBarTone;
  setAppBarTone: (tone: AppBarTone) => void;
  /** Replaces the app bar's menu button with a back button while it is set. */
  backAction: (() => void) | null;
  setBackAction: (action: (() => void) | null) => void;
}

const HeaderContext = createContext<HeaderContextValue | undefined>(undefined);

export interface HeaderProviderProps {
  children: ReactNode;
}

export function HeaderProvider({ children }: HeaderProviderProps) {
  const [mobileActionButton, setMobileActionButton] =
    useState<ReactNode | null>(null);
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const [appBarTone, setAppBarTone] = useState<AppBarTone>('focus');
  const [backAction, setBackAction] = useState<(() => void) | null>(null);

  const value = {
    mobileActionButton,
    setMobileActionButton: useCallback((button: ReactNode | null) => {
      setMobileActionButton(button);
    }, []),
    pageTitle,
    setPageTitle: useCallback((title: string | null) => {
      setPageTitle(title);
    }, []),
    appBarTone,
    setAppBarTone,
    backAction,
    // A state setter reads a bare function as an updater, so wrap it.
    setBackAction: useCallback((action: (() => void) | null) => {
      setBackAction(() => action);
    }, []),
  };

  return (
    <HeaderContext.Provider value={value}>{children}</HeaderContext.Provider>
  );
}

// Hook can be in same file since it's tightly coupled to the context
export function useHeader() {
  const context = useContext(HeaderContext);

  if (context === undefined) {
    throw new Error('useHeader must be used within a HeaderProvider');
  }

  return context;
}
